import styled, { keyframes } from 'styled-components';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

const slideDownAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUpAndFade = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-8px);
  }
`;

export const DropdownTrigger = styled(DropdownMenu.Trigger)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 180px;
  max-width: 250px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(14, 135, 80, 0.4);
  }

  &[data-state='open'] {
    background: rgba(255, 255, 255, 0.2);
    border-color: ${(props) => props.theme['green-300']};
  }
`;

export const TriggerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  overflow: hidden;
`;

export const FarmIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, ${(props) => props.theme['green-500']} 0%, ${(props) => props.theme['green-700']} 100%);
  border-radius: 8px;
  flex-shrink: 0;

  svg {
    color: white;
    font-size: 16px;
  }
`;

export const FarmInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;
`;

export const FarmLabel = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
`;

export const FarmName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
`;

export const ChevronIcon = styled.div<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0)')};

  svg {
    font-size: 14px;
  }
`;

export const DropdownContent = styled(DropdownMenu.Content)`
  min-width: 260px;
  background: white;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 10px 38px -10px rgba(22, 23, 24, 0.35), 0 10px 20px -15px rgba(22, 23, 24, 0.2);
  animation: ${slideDownAndFade} 0.2s ease;
  z-index: 1000;

  &[data-state='closed'] {
    animation: ${slideUpAndFade} 0.2s ease;
  }
`;

export const DropdownLabel = styled(DropdownMenu.Label)`
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${(props) => props.theme['gray-500']};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const DropdownItem = styled(DropdownMenu.Item)<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
  background: ${({ $isSelected, theme }) => ($isSelected ? theme['green-50'] : 'transparent')};
  border: 1px solid ${({ $isSelected, theme }) => ($isSelected ? theme['green-200'] : 'transparent')};

  &:hover {
    background: ${(props) => props.theme['green-50']};
  }

  &:focus {
    background: ${(props) => props.theme['green-100']};
  }

  &[data-highlighted] {
    background: ${(props) => props.theme['green-50']};
  }
`;

export const ItemIcon = styled.div<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: ${({ $isSelected, theme }) => ($isSelected ? `linear-gradient(135deg, ${theme['green-500']} 0%, ${theme['green-700']} 100%)` : theme['gray-100'])};
  border-radius: 8px;
  flex-shrink: 0;
  transition: all 0.15s ease;

  svg {
    color: ${({ $isSelected }) => ($isSelected ? 'white' : '#666')};
    font-size: 18px;
  }
`;

export const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

export const ItemName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme['gray-800']};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SelectedIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: ${(props) => props.theme['green-500']};
  border-radius: 50%;
  flex-shrink: 0;

  svg {
    color: white;
    font-size: 12px;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: ${(props) => props.theme['gray-500']};
  text-align: center;

  svg {
    font-size: 32px;
    margin-bottom: 8px;
    opacity: 0.5;
  }

  span {
    font-size: 13px;
  }
`;


export const PlaceholderText = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
`;
